// src/shared/changelog/useUnseenRelease.ts
import { useEffect, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { getLatestReleaseGroup } from "./getChangelog";
import { useSeenEntries } from "./useSeenEntries";

/**
 * Whether the newest release still holds something this user has not read, and
 * the date the footer card puts under its label.
 *
 * Powers the "See what's new" card — its tinted unseen state when the rail is
 * expanded, and the dot on the collapsed rail.
 *
 * **The release is a group, not an entry** (CTX-591). It used to ask
 * `getLatestRelease()` for the single newest `entryKey` and compare it against
 * one stored flag, which made the card re-tint and the modal restart from `1 of
 * N` whenever a second entry landed in a month the user had already walked
 * through. Asking `getLatestReleaseGroup()` — the same source the modal and the
 * rail's `New` tag use, which §2.7 requires — lets "unseen" mean *any* entry of
 * the month outside the set.
 *
 * The hook no longer marks anything. Marking is a dismissal decision that
 * belongs to the surface the user dismissed (§2.2), and reading it here is what
 * made opening the modal count as having read it.
 */
export function useUnseenRelease(): {
  /**
   * The newest entry's ISO date, or `""` when there is nothing to show. The
   * card's secondary line, and the emptiness check both surfaces gate on.
   */
  releasedAt: string;
  hasUnseen: boolean;
} {
  const axiosClient = useAxiosAuth0();
  const [{ entryKeys, releasedAt }, setRelease] = useState<{
    entryKeys: string[];
    releasedAt: string;
  }>({ entryKeys: [], releasedAt: "" });
  const { seenEntries } = useSeenEntries();

  /**
   * `getChangelog()` memoises one request per session, so mounting this
   * alongside the page and the rail's tags costs nothing extra — they share the
   * same fetch.
   *
   * A failure is swallowed on purpose: the card is an ambient affordance in the
   * sidebar footer, and the honest degraded state is no card at all rather than
   * an error surfaced somewhere the user never asked about. Whoever opens the
   * page gets the real error message there.
   */
  useEffect(() => {
    let alive = true;
    getLatestReleaseGroup(axiosClient)
      .then((group) => {
        if (!alive || !group) return;
        setRelease({
          entryKeys: group.entries.map((entry) => entry.entryKey),
          // The group is newest-entry-first, so this is the newest entry's own
          // date — the card shows a date, not the month the group covers.
          releasedAt: group.entries[0]?.releasedAt ?? "",
        });
      })
      .catch(() => {
        /* no card */
      });
    return () => {
      alive = false;
    };
  }, [axiosClient]);

  const hasUnseen = entryKeys.some((entryKey) => !seenEntries.has(entryKey));

  return { releasedAt, hasUnseen };
}
